import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import axiosInstance from '../services/axiosInstance';

// ShadCN UI Components
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';

const StockEditForm = ({ isOpen, onClose, onSave, stockId }) => {
  const [entry, setEntry] = useState({
    stockName: '',
    sector: '',
    actions: { buy: false, sell: false, watching: false },
    buyDetails: [{ price: '', date: '', time: '', quantity: '' }],
    sellDetails: [{ price: '', date: '', time: '', quantity: '' }],
    watchDetails: { targetPrice: '', action: 'buy' }
  });
  const [sectors, setSectors] = useState([]);
  const [newSector, setNewSector] = useState('');
  const [sectorInput, setSectorInput] = useState('');
  const [showSectorSuggestions, setShowSectorSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Scroll lock when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && stockId) {
      fetchStockData();
      fetchSectors();
    }
    // eslint-disable-next-line
  }, [isOpen, stockId]);

  const fetchStockData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axiosInstance.get(`/stocks/${stockId}`);
      if (response.status === 200 && response.data.success) {
        const stockData = response.data.stock || {};
        setEntry({
          stockName: stockData.stockName || '',
          sector: stockData.sector || '',
          actions: {
            buy: stockData.actions?.buy || false,
            sell: stockData.actions?.sell || false,
            watching: stockData.actions?.watching || false
          },
          buyDetails: stockData.buyDetails?.length > 0 ? stockData.buyDetails.map(detail => {
            // Parse existing datetime string to separate date and time
            let parsedDate = detail.date || '';
            let parsedTime = '';
            
            if (detail.date && detail.date.includes('T')) {
              const parts = detail.date.split('T');
              if (parts.length === 2) {
                parsedDate = parts[0];
                parsedTime = parts[1];
              }
            }
            
            return {
              ...detail,
              date: parsedDate,
              time: parsedTime
            };
          }) : [{ price: '', date: '', time: '', quantity: '' }],
          sellDetails: stockData.sellDetails?.length > 0 ? stockData.sellDetails.map(detail => {
            // Parse existing datetime string to separate date and time
            let parsedDate = detail.date || '';
            let parsedTime = '';
            
            if (detail.date && detail.date.includes('T')) {
              const parts = detail.date.split('T');
              if (parts.length === 2) {
                parsedDate = parts[0];
                parsedTime = parts[1];
              }
            }
            
            return {
              ...detail,
              date: parsedDate,
              time: parsedTime
            };
          }) : [{ price: '', date: '', time: '', quantity: '' }],
          watchDetails: stockData.watchDetails || { targetPrice: '', action: 'buy' }
        });
        setSectorInput(stockData.sector || '');
        setShowSectorSuggestions(false);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load stock data');
    } finally {
      setLoading(false);
    }
  };

  const fetchSectors = async () => {
    try {
      const response = await axiosInstance.get('/stocks/sectors');
      setSectors(response.data.sectors || []);
    } catch (err) {
      setSectors([]);
    }
  };

  const handleChange = (field, value, index = 0) => {
    setEntry(prev => {
      const newEntry = { ...prev };
      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        if (Array.isArray(newEntry[parent])) {
          newEntry[parent][index] = { ...newEntry[parent][index], [child]: value };
        } else {
          newEntry[parent][child] = value;
        }
      } else {
        newEntry[field] = value;
      }
      return newEntry;
    });
  };

  const addDetailEntry = type => {
    setEntry(prev => ({
      ...prev,
      [type]: [...prev[type], { price: '', date: '', time: '', quantity: '' }]
    }));
  };

  const removeDetailEntry = (type, index) => {
    setEntry(prev => ({
      ...prev,
      [type]: prev[type].filter((_, i) => i !== index)
    }));
  };

  const handleSectorChange = value => {
    setSectorInput(value);
    setShowSectorSuggestions(true);
    handleChange('sector', value);
  };

  const handleSectorSelect = sector => {
    handleChange('sector', sector);
    setSectorInput(sector);
    setShowSectorSuggestions(false);
  };

  const handleAddSector = async () => {
    if (!newSector) return;
    try {
      const response = await axiosInstance.post('/stocks/sectors', { sector: newSector });
      if (response.status === 200) {
        setSectors(prev => [...prev, newSector]);
        setNewSector('');
      }
    } catch (err) {}
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);
      const finalEntry = {
        ...entry,
        sector: sectorInput || entry.sector,
        buyDetails: entry.actions.buy
          ? entry.buyDetails
              .filter(detail => detail.price && detail.quantity)
              .map(detail => {
                const processedDetail = {
                  price: Number(detail.price),
                  quantity: Number(detail.quantity)
                };
                
                // Combine date and time if both are present
                if (detail.date && detail.time) {
                  processedDetail.date = `${detail.date}T${detail.time}`;
                } else if (detail.date) {
                  processedDetail.date = detail.date;
                } else {
                  processedDetail.date = '';
                }
                
                return processedDetail;
              })
          : [],
        sellDetails: entry.actions.sell
          ? entry.sellDetails
              .filter(detail => detail.price && detail.quantity)
              .map(detail => {
                const processedDetail = {
                  price: Number(detail.price),
                  quantity: Number(detail.quantity)
                };
                
                // Combine date and time if both are present
                if (detail.date && detail.time) {
                  processedDetail.date = `${detail.date}T${detail.time}`;
                } else if (detail.date) {
                  processedDetail.date = detail.date;
                } else {
                  processedDetail.date = '';
                }
                
                return processedDetail;
              })
          : [],
        watchDetails: entry.actions.watching
          ? {
              targetPrice: entry.watchDetails.targetPrice
                ? Number(entry.watchDetails.targetPrice)
                : 0,
              action: entry.watchDetails.action || 'buy'
            }
          : {}
      };
      const cleanedEntry = {
        ...finalEntry,
        stockName: finalEntry.stockName || '',
        sector: finalEntry.sector || '',
        exchange: finalEntry.exchange || '',
        actions: finalEntry.actions || { buy: false, sell: false, watching: false }
      };
      if (!cleanedEntry.actions.buy) delete cleanedEntry.buyDetails;
      if (!cleanedEntry.actions.sell) delete cleanedEntry.sellDetails;
      if (!cleanedEntry.actions.watching) delete cleanedEntry.watchDetails;

      const response = await axiosInstance.put(
        `/stocks/${stockId}`,
        cleanedEntry
      );
      if (response.data.success) {
        onSave?.(response.data.stock);
        onClose();
      } else {
        setError(response.data.message || 'Failed to save');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving stock');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{ 
      all: 'unset', 
      fontFamily: 'system-ui, -apple-system, sans-serif',
      color: '#111827',
      backgroundColor: 'transparent'
    }}>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-3xl h-[90vh] flex flex-col bg-white text-gray-900 border border-gray-300 overflow-hidden shadow-2xl" style={{ maxHeight: '90vh', zIndex: 3000 }}>
          <DialogHeader className="flex-shrink-0 border-b border-gray-200 pb-4 bg-white z-10">
            <DialogTitle className="text-xl font-semibold text-gray-900">
              Edit Stock Entry
            </DialogTitle>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={onClose}
              className="absolute top-4 right-4 h-8 w-8 p-0 hover:bg-red-100 hover:scale-110 transition-all duration-200 bg-red-50 border border-red-200 text-red-600 hover:text-red-700 hover:border-red-300"
            >
              <X className="h-4 w-4" />
            </Button>
          </DialogHeader>
          
          <div 
            className="flex-1 px-6 py-4 space-y-6 min-h-0 overflow-y-auto"
            style={{ 
              maxHeight: 'calc(90vh - 140px)',
              overflow: 'auto',
              overflowY: 'auto',
              overflowX: 'hidden',
              scrollbarWidth: 'thin',
              scrollbarColor: '#d1d5db #f3f4f6'
            }}
          >
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-gray-500">Loading...</div>
              </div>
            ) : error ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="text-red-800 mb-3">{error}</div>
                <Button 
                  variant="outline"
                  onClick={fetchStockData}
                  className="border-red-300 text-red-700 hover:bg-red-100"
                >
                  Retry
                </Button>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Basic Info Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="stock-name" className="text-sm font-medium text-gray-700 mb-2 block">Stock Name</Label>
                    <Input
                      id="stock-name"
                      className="bg-white border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                      placeholder="Stock Name"
                      value={entry.stockName}
                      onChange={e => handleChange('stockName', e.target.value)}
                      autoComplete="off"
                    />
                  </div>
                  
                  <div className="relative">
                    <Label htmlFor="sector" className="text-sm font-medium text-gray-700 mb-2 block">Sector</Label>
                    <Input
                      id="sector"
                      className="bg-white border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                      placeholder="Sector"
                      value={sectorInput}
                      onChange={e => handleSectorChange(e.target.value)}
                      autoComplete="off"
                      onFocus={() => setShowSectorSuggestions(!!sectorInput)}
                    />
                    {showSectorSuggestions && (
                      <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto z-30 mt-1">
                        {sectors
                          .filter(s =>
                            s.toLowerCase().includes((sectorInput || '').toLowerCase())
                          )
                          .map((sector, i) => (
                            <div
                              key={i}
                              className="px-3 py-2 cursor-pointer border-b border-gray-100 hover:bg-gray-50 text-sm"
                              onClick={() => handleSectorSelect(sector)}
                            >
                              {sector}
                            </div>
                          ))}
                        <div className="p-3 border-t border-gray-200">
                          <div className="flex gap-2">
                            <Input
                              className="flex-1 bg-white border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                              placeholder="New Sector"
                              value={newSector}
                              onChange={e => setNewSector(e.target.value)}
                            />
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handleAddSector}
                              className="border-gray-300 text-gray-700 hover:bg-gray-50 bg-white"
                            >
                              Add
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Checkboxes */}
                <div className="flex flex-wrap gap-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="buy-checkbox"
                      checked={entry.actions.buy}
                      onCheckedChange={checked => handleChange('actions.buy', checked)}
                      className="border-gray-400 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                    />
                    <Label htmlFor="buy-checkbox" className="font-medium text-gray-900 cursor-pointer">Buy</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="sell-checkbox"
                      checked={entry.actions.sell}
                      onCheckedChange={checked => handleChange('actions.sell', checked)}
                      className="border-gray-400 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600"
                    />
                    <Label htmlFor="sell-checkbox" className="font-medium text-gray-900 cursor-pointer">Sell</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="watching-checkbox"
                      checked={entry.actions.watching}
                      onCheckedChange={checked => handleChange('actions.watching', checked)}
                      className="border-gray-400 data-[state=checked]:bg-purple-600 data-[state=checked]:border-purple-600"
                    />
                    <Label htmlFor="watching-checkbox" className="font-medium text-gray-900 cursor-pointer">Watching</Label>
                  </div>
                </div>

                {/* Buy Details */}
                {entry.actions.buy && (
                  <div className="border border-blue-300 rounded-lg p-4 bg-blue-50">
                    <h4 className="font-semibold text-blue-800 text-base mb-4">Buy Details</h4>
                    {entry.buyDetails.map((detail, index) => (
                      <div key={index} className="mb-4">
                        {entry.buyDetails.length > 1 && (
                          <h5 className="font-medium text-blue-700 text-sm mb-3">Buy Entry {index + 1}</h5>
                        )}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                          <div>
                            <Label>Price</Label>
                            <Input
                              type="number"
                              className="mt-1 bg-white border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                              placeholder="Price"
                              min="0"
                              value={detail.price}
                              onChange={e => handleChange('buyDetails.price', e.target.value, index)}
                            />
                          </div>
                          <div>
                            <Label>Quantity</Label>
                            <Input
                              type="number"
                              className="mt-1 bg-white border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                              placeholder="Quantity"
                              min="0"
                              value={detail.quantity}
                              onChange={e => handleChange('buyDetails.quantity', e.target.value, index)}
                            />
                          </div>
                          <div>
                            <Label>Date</Label>
                            <Input
                              type="date"
                              className="mt-1 bg-white border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                              value={detail.date}
                              onChange={e => handleChange('buyDetails.date', e.target.value, index)}
                            />
                          </div>
                          <div>
                            <Label>Time</Label>
                            <Input
                              type="time"
                              className="mt-1 bg-white border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                              value={detail.time}
                              onChange={e => handleChange('buyDetails.time', e.target.value, index)}
                            />
                          </div>
                        </div>
                        {entry.buyDetails.length > 1 && (
                          <div className="flex justify-end">
                            <Button 
                              variant="destructive" 
                              size="icon"
                              onClick={() => removeDetailEntry('buyDetails', index)}
                              className="bg-red-500 hover:bg-red-600 text-white border-0 h-8 w-8"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                        {index === entry.buyDetails.length - 1 && (
                          <div className="mt-3">
                            <Button 
                              variant="outline"
                              size="sm"
                              onClick={() => addDetailEntry('buyDetails')}
                              className="text-blue-700 border-blue-400 hover:bg-blue-100 bg-white font-medium"
                            >
                              <Plus className="h-4 w-4 mr-2" />
                              Add Buy
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Sell Details */}
                {entry.actions.sell && (
                  <div className="border border-red-200 rounded-lg p-4 bg-red-50">
                    <h4 className="font-semibold text-red-800 text-base mb-4">Sell Details</h4>
                    {entry.sellDetails.map((detail, index) => (
                      <div key={index} className="mb-4">
                        {entry.sellDetails.length > 1 && (
                          <h5 className="font-medium text-red-700 text-sm mb-3">Sell Entry {index + 1}</h5>
                        )}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                          <div>
                            <Label>Price</Label>
                            <Input
                              type="number"
                              className="mt-1 bg-white border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                              placeholder="Price"
                              min="0"
                              value={detail.price}
                              onChange={e => handleChange('sellDetails.price', e.target.value, index)}
                            />
                          </div>
                          <div>
                            <Label>Quantity</Label>
                            <Input
                              type="number"
                              className="mt-1 bg-white border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                              placeholder="Quantity"
                              min="0"
                              value={detail.quantity}
                              onChange={e => handleChange('sellDetails.quantity', e.target.value, index)}
                            />
                          </div>
                          <div>
                            <Label>Date</Label>
                            <Input
                              type="date"
                              className="mt-1 bg-white border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                              value={detail.date}
                              onChange={e => handleChange('sellDetails.date', e.target.value, index)}
                            />
                          </div>
                          <div>
                            <Label>Time</Label>
                            <Input
                              type="time"
                              className="mt-1 bg-white border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                              value={detail.time}
                              onChange={e => handleChange('sellDetails.time', e.target.value, index)}
                            />
                          </div>
                        </div>
                        {entry.sellDetails.length > 1 && (
                          <div className="flex justify-end">
                            <Button 
                              variant="destructive" 
                              size="icon"
                              onClick={() => removeDetailEntry('sellDetails', index)}
                              className="bg-red-500 hover:bg-red-600 text-white border-0 h-8 w-8"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                        {index === entry.sellDetails.length - 1 && (
                          <div className="mt-3">
                            <Button 
                              variant="outline"
                              size="sm"
                              onClick={() => addDetailEntry('sellDetails')}
                              className="text-red-600 border-red-300 hover:bg-red-100 bg-white font-medium"
                            >
                              <Plus className="h-4 w-4 mr-2" />
                              Add Sell
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Watch Details */}
                {entry.actions.watching && (
                  <div className="border border-purple-200 rounded-lg p-4 bg-purple-50">
                    <h4 className="font-medium text-purple-800 mb-3">Watch Details</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <Label>Target Price</Label>
                        <Input
                          type="number"
                          className="mt-1 bg-white border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500"
                          placeholder="Target Price"
                          min="0"
                          value={entry.watchDetails.targetPrice}
                          onChange={e => handleChange('watchDetails.targetPrice', e.target.value)}
                        />
                      </div>
                      <div>
                        <Label>Action</Label>
                        <Select
                          value={entry.watchDetails.action}
                          onValueChange={value => handleChange('watchDetails.action', value)}
                        >
                          <SelectTrigger className="mt-1 bg-white border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-white border-gray-300">
                            <SelectItem value="buy" className="text-gray-900 hover:bg-gray-100">Buy</SelectItem>
                            <SelectItem value="sell" className="text-gray-900 hover:bg-gray-100">Sell</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="flex justify-end items-center pt-4 pb-4 px-6 border-t border-gray-200 flex-shrink-0 bg-white">
            <Button 
              onClick={handleSave}
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 text-white border-0 font-medium px-6"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StockEditForm;
